import { useMemo } from 'react';
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  Slider,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { Link } from 'react-router-dom';
import ContainerTitle from '@/components/general/ContainerTitle';
import CustomLink from '@/components/general/CustomLink';
import PageTitle from '@/components/general/PageTitle';
import {
  QuestionFrequency,
  questionOptionsRange,
  questionTypes,
  RotatingQuestionInputData,
} from '@/types/api';
import { Paths } from '@/types/routes';
import { weekPeriodByDate } from '@/utils/format';
import styles from './ManageRotatingQuestion.styles';

type ManageRotatingQuestionPageProps = {
  isEditManagement: boolean;
  isDateInputOpen: boolean;
  isOptionsInputOpen: boolean;
  questionData: RotatingQuestionInputData | null;
  handleChangeData: (input: keyof RotatingQuestionInputData, value: any) => void;
  handleChangeOptionsQuantity: (event: Event, newValue: number | number[]) => void;
  handleManageQuestion: () => void;
};

const ManageRotatingQuestionPage = function ({
  isEditManagement,
  isDateInputOpen,
  isOptionsInputOpen,
  questionData,
  handleChangeData,
  handleChangeOptionsQuantity,
  handleManageQuestion,
}: ManageRotatingQuestionPageProps): JSX.Element {
  const titlePage = useMemo(
    () => (isEditManagement ? 'Edit the question' : 'Create a new question'),
    [isEditManagement]
  );

  const buttonLabel = useMemo(
    () => (isEditManagement ? 'Edit question' : 'Create question'),
    [isEditManagement]
  );

  const [checkinPeriodStartDate, checkinPeriodEndDate] = useMemo(
    () => (questionData?.scheduledAt ? weekPeriodByDate(questionData?.scheduledAt) : [null, null]),
    [questionData?.scheduledAt]
  );

  const quantityOfNumberOptions = useMemo(
    () => questionData?.options?.length ?? 5,
    [questionData?.options?.length]
  );

  return (
    <Box data-testid="manage-rotating-questions-page">
      <CustomLink to={Paths.ROTATING_QUESTIONS} variant="subtitle1" data-testid="go-back-link">
        go back
      </CustomLink>

      <Box display="flex" alignItems="flex-end" justifyContent="space-between">
        <PageTitle title={titlePage} data-testid="manage-question-title-page" />
      </Box>

      <Paper variant="outlined" sx={styles.queueContainer} data-testid="queue-container">
        <ContainerTitle title="Question details" />

        <Box>
          <TextField
            sx={styles.textField}
            value={questionData?.answerType}
            onChange={(event) => handleChangeData('answerType', event.target.value)}
            select
            size="small"
            variant="outlined"
            label="Select a question type"
            fullWidth
          >
            {questionTypes.map((type) => (
              <MenuItem key={type.key} value={type.value}>
                {type.text}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            sx={styles.textField}
            value={questionData?.label}
            onChange={(event) => handleChangeData('label', event.target.value)}
            size="small"
            variant="outlined"
            label="Question"
            placeholder="Example: What is something that always make your day?"
            fullWidth
            data-testid="question-input"
          />

          <TextField
            sx={styles.textField}
            value={questionData?.description}
            onChange={(event) => handleChangeData('description', event.target.value)}
            size="small"
            variant="outlined"
            label="Description"
            placeholder="Example: This question will help us to know you better"
            fullWidth
            data-testid="description-input"
          />

          {isOptionsInputOpen && (
            <Box marginTop={3}>
              <Typography>
                Select the range of options for this question (1 - {quantityOfNumberOptions})
              </Typography>

              <Slider
                min={1}
                max={10}
                step={1}
                marks={questionOptionsRange}
                value={quantityOfNumberOptions}
                valueLabelDisplay="off"
                onChange={handleChangeOptionsQuantity}
              />
            </Box>
          )}

          <Box display="flex" flexDirection="column" marginTop={2}>
            <FormControlLabel
              value={questionData?.isRequired}
              onChange={(event, checked) => handleChangeData('isRequired', checked)}
              control={<Checkbox checked={questionData?.isRequired} />}
              label="Required question"
            />

            <FormControlLabel
              value={questionData?.isActive}
              onChange={(event, checked) => handleChangeData('isActive', checked)}
              control={<Checkbox disabled={!isEditManagement} checked={questionData?.isActive} />}
              label="Active question"
            />
          </Box>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={styles.queueContainer} data-testid="queue-container">
        <ContainerTitle title="How often is this question asked?" />

        <FormControl>
          <RadioGroup
            name="how-often-radio-buttons-group"
            defaultValue={QuestionFrequency.QUESTION_QUEUED}
            value={questionData?.questionFrequency}
            onChange={(event, value) => handleChangeData('questionFrequency', value)}
          >
            <FormControlLabel
              value={QuestionFrequency.ONE_TIME_QUESTION}
              control={<Radio />}
              label="This is a one-time question"
            />

            <FormControlLabel
              value={QuestionFrequency.QUESTION_QUEUED}
              control={<Radio />}
              label="This question is queued"
            />

            <Box display="flex" flexDirection="column" data-testid="often-question-date-radio">
              <FormControlLabel
                value={QuestionFrequency.SCHEDULED}
                control={<Radio />}
                label="Schedule this question"
                sx={{ marginBottom: isDateInputOpen ? 1 : 0 }}
              />

              {isDateInputOpen && (
                <Box paddingLeft={4} display="flex" alignItems="center" data-testid="date-input">
                  <DesktopDatePicker
                    minDate={new Date()}
                    label="Date to this question be asked"
                    value={questionData?.scheduledAt}
                    onChange={(newDate) => handleChangeData('scheduledAt', newDate)}
                    renderInput={(params) => <TextField size="small" {...params} />}
                  />

                  {!!questionData?.scheduledAt && (
                    <Typography component="span" color="GrayText" fontSize={12} marginLeft={2}>
                      {checkinPeriodStartDate} - {checkinPeriodEndDate}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </RadioGroup>
        </FormControl>
      </Paper>

      <Box display="flex" alignItems="center" justifyContent="flex-start">
        <Button
          variant="outlined"
          onClick={handleManageQuestion}
          disabled={!questionData?.label}
          data-testid="manage-question-action-button"
        >
          {buttonLabel}
        </Button>

        <Button
          color="auxRed"
          sx={styles.cancelButton}
          component={Link}
          to={Paths.ROTATING_QUESTIONS}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default ManageRotatingQuestionPage;
